import { Head, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { RoleBadge } from "@/components/role-badge"
import { StatusIndicator } from "@/components/status-indicator"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { UserDisplay } from "@/components/user-display"
import { UserFormDialog } from "@/components/user-form-dialog"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { useDebouncedSearch } from "@/hooks/use-debounced-search"
import { Plus, Search, Pencil, LogIn, Users } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { UserSummary, PagyMetadata, SharedProps } from "@/types"

type Props = {
  users: UserSummary[]
  filters: Record<string, string>
  pagy: PagyMetadata
  organisations: { id: number | string; name: string }[]
  teams: { id: number | string; name: string; organisation_id: number | string }[]
}

function UsersIndex({ users, filters, pagy, organisations, teams }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const canCreate = current_user?.is_super_admin || current_user?.role === "admin"

  const [search, setSearch, searchRef] = useDebouncedSearch(
    filters.email_address_or_first_name_or_last_name_cont || "",
    "/users",
    "email_address_or_first_name_or_last_name_cont",
  )

  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<UserSummary | null>(null)
  const [viewingUser, setViewingUser] = useState<UserSummary | null>(null)
  const [impersonatingUser, setImpersonatingUser] = useState<UserSummary | null>(null)
  const [impersonationReason, setImpersonationReason] = useState("")

  return (
    <>
      <Head title="Utilisateurs" />
      <PageHeader title="Utilisateurs" />
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchRef}
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
            </Badge>
            {canCreate && (
              <Button
                onClick={() => {
                  setEditingUser(null)
                  setShowForm(true)
                }}
              >
                <Plus className="size-4" />
                Nouvel utilisateur
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="px-0">
            {users.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Aucun utilisateur trouvé"
                description="Essayez de modifier vos critères de recherche."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          className="hover:underline text-left"
                          onClick={() => setViewingUser(user)}
                        >
                          <UserDisplay user={user} size="sm" />
                        </button>
                        {user.is_super_admin && (
                          <Badge variant="destructive" className="ml-2">
                            Super Admin
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{user.email_address}</TableCell>
                      <TableCell>{user.organisation?.name || "—"}</TableCell>
                      <TableCell>
                        <RoleBadge role={user.role} />
                      </TableCell>
                      <TableCell>
                        <StatusIndicator active={user.is_active} />
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {current_user?.is_super_admin && user.id !== current_user.id && !user.is_super_admin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setImpersonatingUser(user)
                              setImpersonationReason("")
                            }}
                            title="Se connecter en tant que cet utilisateur"
                          >
                            <LogIn className="size-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingUser(user)
                            setShowForm(true)
                          }}
                          title="Modifier"
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <Pagination pagy={pagy} baseUrl="/users" filters={filters} className="w-full p-0" />
          </CardFooter>
        </Card>
      </div>

      {/* Form modal */}
      <UserFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        user={editingUser}
        organisations={organisations || []}
        teams={teams || []}
      />

      {/* Impersonation confirmation dialog */}
      <Dialog
        open={!!impersonatingUser}
        onOpenChange={(open) => {
          if (!open) setImpersonatingUser(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Se connecter en tant que {impersonatingUser?.first_name} {impersonatingUser?.last_name}
            </DialogTitle>
            <DialogDescription>Veuillez indiquer la raison de cette connexion.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <Label htmlFor="impersonation-reason">Raison</Label>
            <Input
              id="impersonation-reason"
              placeholder="Ex : Vérification d'un bug signalé par l'utilisateur"
              value={impersonationReason}
              onChange={(e) => setImpersonationReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && impersonationReason.trim() && impersonatingUser) {
                  router.post(`/users/${impersonatingUser.id}/impersonation`, { reason: impersonationReason })
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonatingUser(null)}>
              Annuler
            </Button>
            <Button
              disabled={!impersonationReason.trim()}
              onClick={() => {
                if (impersonatingUser) {
                  router.post(`/users/${impersonatingUser.id}/impersonation`, { reason: impersonationReason })
                }
              }}
            >
              <LogIn className="size-4" />
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail modal */}
      <Dialog
        open={!!viewingUser}
        onOpenChange={(open) => {
          if (!open) setViewingUser(null)
        }}
      >
        <DialogContent>
          {viewingUser && (
            <>
              <DialogHeader>
                <DialogTitle>
                  <UserDisplay user={viewingUser} size="lg" showEmail />
                </DialogTitle>
              </DialogHeader>
              <DialogBody className="space-y-4">
                <div className="flex items-center gap-2">
                  <StatusIndicator active={viewingUser.is_active} />
                  {viewingUser.is_super_admin && <Badge variant="destructive">Super Admin</Badge>}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground text-sm">Rôle</p>
                    <RoleBadge role={viewingUser.role} />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Organisation</p>
                    <p>{viewingUser.organisation?.name || "—"}</p>
                  </div>
                  {viewingUser.job_title && (
                    <div>
                      <p className="text-muted-foreground text-sm">Poste</p>
                      <p>{viewingUser.job_title}</p>
                    </div>
                  )}
                  {viewingUser.phone_number && (
                    <div>
                      <p className="text-muted-foreground text-sm">Téléphone</p>
                      <p>{viewingUser.phone_number}</p>
                    </div>
                  )}
                </div>

                {viewingUser.teams && viewingUser.teams.length > 0 && (
                  <div>
                    <p className="text-muted-foreground mb-2 text-sm">Équipes</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingUser.teams.map((team) => (
                        <Badge key={team.id} variant="secondary">
                          {team.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      setViewingUser(null)
                      setEditingUser(viewingUser)
                      setShowForm(true)
                    }}
                  >
                    <Pencil className="mr-2 size-3.5" />
                    Modifier
                  </Button>
                </div>
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

UsersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default UsersIndex
